import React, { FC, useEffect, useMemo, useState } from 'react';
import { EditPreferences } from 'Admin/preferences/SupplierPreferences/EditPreferences';
import { ReadOnlyPreferences } from 'Admin/preferences/SupplierPreferences/ReadOnlyPreferences';
import { useSelector } from 'react-redux';
import { selectCompanyPreferences, selectNameOfSelectedCompany, selectSelectedCompanyId } from 'User/selectors';
import Alert from 'shared/components/atoms/Alert';
import { Supplier } from 'Quotes/types/supplier';
import SpinnerWithText from 'shared/components/molecules/SpinnerWithText';
import { pages, useTrackPage } from 'shared/utils/trackPage';
import { useCloudProvidersFetch } from 'Location/CloudConnect/CloudProviders/hooks';

enum FetchCustomerStatus {
  BEFORE_FETCHING = 'BEFORE_FETCHING',
  FETCHING_COMPLETE = 'FETCHING_COMPLETE',
}

// These are the suppliers from Supplier object that shouldn't be editable so users can never exclude Neos.
export const ExcludedSuppliers = [Supplier.ONAT, Supplier.NNAT, Supplier.VODAFONE, Supplier.SKY_ITS];

export interface SupplierList {
  key: string;
  value: Supplier;
  status: boolean;
}

const mergedSuppliers: Record<string, Supplier[]> = {
  ITS: [Supplier.ITS_FIBRELIGHT, Supplier.ITS_FIBREBRIGHT],
};

const INTRO_TEXT = (companyName: string) => {
  return (
    <>
      <p>These preferences control which suppliers are shown when quoting for {companyName}.</p>
      <p>
        This change will not affect any current quotes that include the deactivated supplier and includes any edits of
        existing quotes. Only new quotes will be affected. The deactivated supplier can still be ordered on any valid
        quotes.
      </p>
      <p>
        Note that results from Neos Networks are always shown, but the more suppliers chosen will provide a broader
        range of quotes.
      </p>
    </>
  );
};

const ERROR_MESSAGE = 'Error in retrieving customer preference';

export const SupplierPreferences: FC = () => {
  useTrackPage(pages.customerPreferences);

  const companyId = useSelector(selectSelectedCompanyId);
  const companyName = useSelector(selectNameOfSelectedCompany);
  const companyPreferences = useSelector(selectCompanyPreferences);

  const introText = useMemo(() => INTRO_TEXT(companyName), [companyName]);

  const [isReadOnly, setReadOnly] = useState(true);
  const [isSaved, setSaved] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [fetchCustomerStatus, setFetchCustomerStatus] = useState(FetchCustomerStatus.BEFORE_FETCHING);
  const [displaySupplierList, setDisplaySupplierList] = useState<SupplierList[]>();
  const [cloudConnectProviders, setCloudConnectProviders] = useState<SupplierList[]>();

  const updatePageStatus = (readOnly: boolean, saved: boolean) => {
    setReadOnly(readOnly);
    setSaved(saved);
  };

  const [allCloudProviders, setAllCloudProviders] = useState<any[]>([]);
  const [cloudProvidersLoaded, setCloudProvidersLoaded] = useState(false);

  // Fetch all cloud providers
  useCloudProvidersFetch({
    onSuccess: (data: any[]) => {
      setAllCloudProviders(
        data
          .filter((provider: any) => provider.attributes.is_enabled)
          .map((provider: any) => ({
            key: provider.attributes.name,
            value: provider.attributes.usage,
          }))
      );
      setCloudProvidersLoaded(true);
    },
    customerId: '',
  });

  useEffect(() => {
    // Keep showing loading if companyPreferences is null
    if (!companyId || !cloudProvidersLoaded || !companyPreferences) return;

    let isMounted = true;
    (async () => {
      setFetchCustomerStatus(FetchCustomerStatus.BEFORE_FETCHING);
      setReadOnly(true);
      setFetchError(false);
      setSaved(false);
      setDisplaySupplierList(undefined);
      setCloudConnectProviders(undefined);

      try {
        // Loop through local Supplier object and check if each supplier is present in the API response to set the
        // status to true or false.
        const filteredAndCombinedSupplierList = Object.keys(Supplier)
          .map((key) => {
            return {
              key: key,
              value: Supplier[key as keyof typeof Supplier],
              status:
                // true = supplier in API response | false = supplier not in API response.
                typeof companyPreferences?.allowed_suppliers.find(
                  (supplier) => supplier === Supplier[key as keyof typeof Supplier]
                ) !== 'undefined',
            };
          })
          .filter((supplier) => !ExcludedSuppliers.includes(supplier.value))
          .filter((supplier) => supplier.value !== Supplier.NONE) // Neos (None) can not be disabled
          .filter((supplier) => !Object.values(mergedSuppliers).flat().includes(supplier.value));

        const mergedSupplierList: SupplierList[] = Object.entries(mergedSuppliers).map(
          ([mergedSupplierKey, mergedValues]) => ({
            key: mergedSupplierKey,
            value: mergedValues[0],
            status: mergedValues.some((supplier) => companyPreferences.allowed_suppliers.includes(supplier)),
          })
        );

        const displaySuppliers = [...filteredAndCombinedSupplierList, ...mergedSupplierList];

        const enabledCloudProviderNames = companyPreferences?.cloud_connect_providers
            ?.filter((provider: any) => provider.attributes.is_enabled)
            .map((provider: any) => provider.attributes.name) ?? [];
    
        // Map all enabled cloud providers, set status true if in responseCloudConnectProviders
        const cloudConnectProvidersList: SupplierList[] = allCloudProviders.map((provider) => ({
          key: provider.key,
          value: provider.value,
          status: enabledCloudProviderNames.includes(provider.key),
        }));

        if (isMounted) {
          setDisplaySupplierList(displaySuppliers);
          setCloudConnectProviders(cloudConnectProvidersList);
        }
      } catch (error) {
        setFetchError(true);
      } finally {
        setFetchCustomerStatus(FetchCustomerStatus.FETCHING_COMPLETE);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [companyId, cloudProvidersLoaded, allCloudProviders, companyPreferences]);

  return (
    <div>
      {(fetchCustomerStatus === FetchCustomerStatus.BEFORE_FETCHING || !companyPreferences) && (
        <SpinnerWithText text="Retrieving preferences" size="large" />
      )}

      {fetchError && <Alert>{ERROR_MESSAGE}</Alert>}

      {fetchCustomerStatus === FetchCustomerStatus.FETCHING_COMPLETE && !fetchError && companyPreferences
        ? isReadOnly
          ? displaySupplierList && (
              <ReadOnlyPreferences
                introText={introText}
                suppliers={displaySupplierList}
                cloudConnectProviders={cloudConnectProviders}
                updatePageStatus={updatePageStatus}
                saved={isSaved}
              />
            )
          : displaySupplierList && (
              <EditPreferences
                introText={introText}
                suppliers={displaySupplierList}
                cloudConnectProviders={cloudConnectProviders}
                updatePageStatus={updatePageStatus}
                updateSuppliersState={setDisplaySupplierList}
                updateCloudConnectProvidersState={setCloudConnectProviders}
              />
            )
        : ''}
    </div>
  );
};
